/**
 * SearchBar Component
 * 
 * A search input component with debounced onChange handler for filtering files and folders.
 * Supports both local folder filtering and global search across all folders.
 * 
 * **Feature: file-explorer-improvements**
 * **Validates: Requirement 7.1** - Display a search input field prominently in the interface
 * **Validates: Requirement 7.3** - Global search toggle to search across all folders
 * **Validates: Requirement 7.6** - Perform search filtering with minimal delay as the user types
 * 
 * @module SearchBar
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import './SearchBar.css';

/**
 * Default debounce delay in milliseconds.
 * This prevents excessive filtering while the user is typing.
 */
export const DEFAULT_DEBOUNCE_DELAY = 300;

/**
 * Custom hook for debouncing a value.
 * Returns the debounced value after the specified delay.
 * 
 * @param {string} value - The value to debounce
 * @param {number} delay - The debounce delay in milliseconds
 * @returns {string} The debounced value
 */
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // Set up a timer to update the debounced value after the delay
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Clean up the timer if value changes before delay completes
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * SearchBar component for filtering files and folders.
 * 
 * Features:
 * - Text input with search icon (magnifying glass)
 * - Clear button (X) that appears when input has value
 * - Debounced onChange handler (300ms delay) to avoid excessive filtering
 * - Placeholder text "Search files..."
 * - Accessible labels and keyboard support
 * - Global search toggle to search across all folders (Requirement 7.3)
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Current search query value
 * @param {Function} props.onChange - Callback when search query changes (debounced)
 * @param {Function} props.onClear - Callback when clear button is clicked
 * @param {string} [props.placeholder='Search files...'] - Placeholder text for the input
 * @param {number} [props.debounceDelay=300] - Debounce delay in milliseconds
 * @param {boolean} [props.disabled=false] - Whether the search input is disabled
 * @param {string} [props.className=''] - Additional CSS class names
 * @param {boolean} [props.isGlobalSearch=false] - Whether global search mode is enabled
 * @param {Function} [props.onGlobalSearchToggle] - Callback when global search toggle changes
 * @param {boolean} [props.showGlobalToggle=true] - Whether to show the global search toggle
 * @returns {JSX.Element} The SearchBar component
 */
const SearchBar = ({
  value,
  onChange,
  onClear,
  placeholder = 'Search files...',
  debounceDelay = DEFAULT_DEBOUNCE_DELAY,
  disabled = false,
  className = ''
}) => {
  // Internal state for the input value (updates immediately)
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef(null);
  const isClearing = useRef(false);

  // Debounce the input value
  const debouncedValue = useDebounce(inputValue, debounceDelay);

  // Sync internal state with external value prop (but not during clear)
  useEffect(() => {
    if (!isClearing.current) {
      setInputValue(value);
    }
  }, [value]);

  // Call onChange when debounced value changes
  useEffect(() => {
    // Only call onChange if the debounced value differs from the external value
    if (debouncedValue !== value) {
      onChange(debouncedValue);
    }
    // Reset clearing flag after debounce completes
    if (isClearing.current && debouncedValue === '') {
      isClearing.current = false;
    }
  }, [debouncedValue, onChange, value]);

  /**
   * Handle input change event.
   * Updates internal state immediately for responsive UI.
   * 
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event
   */
  const handleInputChange = useCallback((event) => {
    setInputValue(event.target.value);
  }, []);

  /**
   * Handle clear button click.
   * Clears the input and calls onClear callback.
   */
  const handleClear = useCallback(() => {
    isClearing.current = true;
    setInputValue('');
    onChange('');
    if (onClear) {
      onClear();
    }
    // Focus the input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onChange, onClear]);

  /**
   * Handle keyboard events for accessibility.
   * - Escape: Clear the search input
   * 
   * @param {React.KeyboardEvent<HTMLInputElement>} event - The keyboard event
   */
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape' && inputValue) {
      event.preventDefault();
      handleClear();
    }
  }, [inputValue, handleClear]);

  const hasValue = inputValue && inputValue.length > 0;

  return (
    <div className={`search-bar-wrapper ${className}`}>
      <div className={`search-bar ${disabled ? 'search-bar-disabled' : ''} ${className}`}>
        {/* Search icon (magnifying glass) */}
        <span className="search-bar-icon" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </span>

        {/* Search input */}
        <input
          ref={inputRef}
          type="text"
          className="search-bar-input"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          aria-label="Search files and folders"
          role="searchbox"
          autoComplete="off"
          spellCheck="false"
        />

        {/* Clear button - only visible when input has value */}
        {hasValue && !disabled && (
          <button
            type="button"
            className="search-bar-clear"
            onClick={handleClear}
            aria-label="Clear search"
            title="Clear search (Escape)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

SearchBar.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  placeholder: PropTypes.string,
  debounceDelay: PropTypes.number,
  disabled: PropTypes.bool,
  className: PropTypes.string
};

export default SearchBar;
